import { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import "react-easy-crop/react-easy-crop.css";
import { useTranslation } from "react-i18next";

import useLayoutStore from "../../store/useLayoutStore";
import { getCroppedEmployeeImageBlob } from "../../utils/employeeImage";

const EmployeeImageCropModal = ({ imageSrc, onConfirm, onClose }) => {
    const { t } = useTranslation("settings");
    const isDarkTheme = useLayoutStore((state) => state.isDarkTheme);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState("");

    const handleCropComplete = useCallback((_, pixels) => {
        setCroppedAreaPixels(pixels);
    }, []);

    const handleConfirm = async () => {
        if (!croppedAreaPixels) return;

        setIsProcessing(true);
        setError("");

        try {
            const blob = await getCroppedEmployeeImageBlob(imageSrc, croppedAreaPixels);
            onConfirm(blob);
        } catch {
            setError(t("crop.processFailed"));
            setIsProcessing(false);
        }
    };

    return (
        <div className="st-image-crop-overlay">
            <div
                className={`st-image-crop-backdrop ${isDarkTheme ? "st-image-crop-backdrop--dark" : ""}`}
                onClick={onClose}
                aria-hidden="true"
            />
            <div
                className={`st-image-crop-modal ${isDarkTheme ? "dark-dv-edit-modal" : "white-dv-edit-modal"}`}
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    type="button"
                    className={`dv-edit-modal-close-button ${isDarkTheme ? "filter-dark-buttons" : ""}`}
                    onClick={onClose}
                    aria-label={t("crop.closeAria")}
                >
                    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: "block", margin: "auto" }}>
                        <line x1="7.2" y1="7.2" x2="20.8" y2="20.8" stroke="#08293e" strokeWidth="2.4" strokeLinecap="round" />
                        <line x1="20.8" y1="7.2" x2="7.2" y2="20.8" stroke="#08293e" strokeWidth="2.4" strokeLinecap="round" />
                    </svg>
                </button>

                <h3>{t("crop.title")}</h3>
                <p className="st-image-crop-hint">{t("crop.hint")}</p>

                <div className="st-image-crop-area">
                    <Cropper
                        image={imageSrc}
                        crop={crop}
                        zoom={zoom}
                        aspect={1}
                        cropShape="round"
                        showGrid={false}
                        onCropChange={setCrop}
                        onZoomChange={setZoom}
                        onCropComplete={handleCropComplete}
                    />
                </div>

                <label className="st-image-crop-zoom-label">
                    {t("crop.zoom")}
                    <input
                        type="range"
                        min={1}
                        max={3}
                        step={0.01}
                        value={zoom}
                        onChange={(e) => setZoom(Number(e.target.value))}
                    />
                </label>

                {error && <div className="st-employee-form-error st-image-crop-error">{error}</div>}

                <div className="st-image-crop-actions">
                    <button
                        type="button"
                        className={`dv-delete-confirm-modal-button cancel-btn ${isDarkTheme ? "dark-btn-close" : ""}`}
                        onClick={onClose}
                        disabled={isProcessing}
                    >
                        {t("crop.cancel")}
                    </button>
                    <button
                        type="button"
                        className={`dv-edit-save-button ${isDarkTheme ? "dark-big-btn" : ""}`}
                        onClick={handleConfirm}
                        disabled={isProcessing || !croppedAreaPixels}
                    >
                        {isProcessing ? t("crop.processing") : t("crop.confirm")}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EmployeeImageCropModal;
